import React, { useEffect, useState } from 'react';
import { Download, History } from 'lucide-react';
import apiService from '../../services/apiService';

const EVENT_FILTERS = [
  { value: 'all', label: 'All events' },
  { value: 'adjust', label: 'Adjust' },
  { value: 'submit', label: 'Submit' },
  { value: 'return', label: 'Return' },
  { value: 'approve', label: 'Approve' }
];

const fmtWhen = (value) => String(value || '').slice(0, 16).replace('T', ' ');

const renderValue = (value) => {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * B6 — paginated audit trail for a submission. Seeds from the detail record's
 * events for fast first paint, then pages the server-side events endpoint.
 * Supports an event filter, a CSV export link, and a structural payload diff.
 */
const ForecastAuditTrail = ({ submissionId, initialEvents }) => {
  const [events, setEvents] = useState(initialEvents || []);
  const [cursor, setCursor] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [diffs, setDiffs] = useState({}); // eventId -> { status, rows }

  const fetchPage = async ({ reset = false, useCursor = null } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.fetchForecastSubmissionEvents(submissionId, {
        limit: 50,
        includePayload: true,
        ...(filter !== 'all' ? { event: filter } : {}),
        ...(useCursor ? { cursor: useCursor } : {})
      });
      const rows = result?.events || [];
      setEvents((previous) => (reset ? rows : [...previous, ...rows]));
      setCursor(result?.page?.nextCursor || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  // Load the first (authoritative, paginated) page on mount and whenever the
  // filter or submission changes. Seeded initialEvents give a fast first paint
  // until this resolves.
  useEffect(() => {
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, submissionId]);

  const toggleDiff = async (event) => {
    if (diffs[event.id]) {
      setDiffs((previous) => {
        const next = { ...previous };
        delete next[event.id];
        return next;
      });
      return;
    }
    setDiffs((previous) => ({ ...previous, [event.id]: { status: 'loading', rows: [] } }));
    try {
      const result = await apiService.fetchForecastSubmissionEventDiff(submissionId, event.id, {});
      setDiffs((previous) => ({ ...previous, [event.id]: { status: 'done', rows: result?.diff || [] } }));
    } catch (err) {
      setDiffs((previous) => ({
        ...previous,
        [event.id]: { status: 'error', rows: [], message: err?.response?.data?.error || err.message }
      }));
    }
  };

  const csvHref = apiService.forecastSubmissionEventsCsvUrl(submissionId, {
    includePayload: true,
    ...(filter !== 'all' ? { event: filter } : {})
  });

  return (
    <section className="forecast-panel forecast-audit-trail">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">RV_AUD ● Audit trail</span>
        <h2>
          <History size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Full submission event history
        </h2>
        <span className="forecast-submit-chips" style={{ marginLeft: 'auto' }}>
          <label className="forecast-audit-filter">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              {EVENT_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <a className="forecast-secondary-button compact" href={csvHref} download>
            <Download size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
            Export CSV
          </a>
        </span>
      </div>

      {error && <p className="forecast-submit-note" style={{ color: 'var(--ace-negative, #d9534f)' }}>{error}</p>}

      <div className="forecast-home-table-wrap">
        <table className="forecast-home-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Actor</th>
              <th>Comment</th>
              <th>When</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr><td colSpan={5}><span className="dim-note">No events recorded.</span></td></tr>
            ) : (
              events.map((event) => {
                const diff = diffs[event.id];
                return (
                  <React.Fragment key={event.id ?? `${event.event}-${event.createdAt || event.created_at}`}>
                    <tr>
                      <td><span className="forecast-health-chip neutral mini">{event.event}</span></td>
                      <td>{event.actor || '—'}</td>
                      <td>{event.comment || <span className="dim-note">—</span>}</td>
                      <td className="forecast-mono">{fmtWhen(event.createdAt || event.created_at)}</td>
                      <td>
                        {event.payload && event.id !== undefined && event.id !== null && (
                          <button type="button" className="forecast-home-action ghost" onClick={() => toggleDiff(event)}>
                            {diff ? 'Hide diff' : 'Diff'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {diff && (
                      <tr className="forecast-audit-diff-row">
                        <td colSpan={5}>
                          {diff.status === 'loading' ? (
                            <span className="dim-note">Loading diff…</span>
                          ) : diff.status === 'error' ? (
                            <span className="neg">{diff.message}</span>
                          ) : diff.rows.length === 0 ? (
                            <span className="dim-note">No structural changes in this payload.</span>
                          ) : (
                            <ul className="forecast-audit-diff-list">
                              {diff.rows.map((row) => (
                                <li key={row.path}>
                                  <span className="forecast-mono">{row.path}</span>
                                  <span className="forecast-diff-arrow"> </span>
                                  <span className="forecast-mono neg">{renderValue(row.before)}</span>
                                  <span className="forecast-diff-arrow"> → </span>
                                  <strong className="forecast-mono pos">{renderValue(row.after)}</strong>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {cursor && (
        <div className="forecast-submit-chips" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="forecast-secondary-button compact"
            disabled={loading}
            onClick={() => fetchPage({ useCursor: cursor })}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
};

export default ForecastAuditTrail;
